import React, { useEffect, useState } from 'react';
import {
  createStyles, MantineProvider,
  useMantineTheme,
} from '@mantine/core';
import {
  Routes, Route, useNavigate,
  useLocation,
} from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
// import { useHotkeys } from '@mantine/hooks';
import { Notifications } from "@mantine/notifications";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useMediaQuery } from '@mantine/hooks';
import { Home } from './features/home';
import {
  ChangeDob, LinkBankAccount, PanCardVerify, PersonalDetails, PersonalImageVerification,
  AddEmail, VerifyEmail, Signup, SignupVerification, SignOnboardingForm,
  NomineeDetails, IncomeProofForm, KycVerification, PanUpload, AadhaarEsignForm, Modification,
} from './features/pages';
import { RootState } from './redux/store';
import { clearData, loadData } from './utils/localStorage';
import {
  getUserDetails,
  setAccountStatus,
  verifyPrevTokenAndGetUserDetails,
  setMobileNumber,
} from './features/account/accountSlice';
import { RouteManager } from './RouteManager';
import { ScreenLoader } from './components/screenLoader';
import Completed from './features/account/completed';
import Navbar from './components/navbar/navbar';
import { getCustomTheme } from './theme/theme';
import GlobalStyles from './theme/globalStyles';
import './styles/styles.scss'
import "dayjs/locale/es";
import { useCleverTap } from './hooks';
import { RequireAuth } from './components';

dayjs.extend(customParseFormat);

const useStyles = createStyles((themes) => ({
  canvas: {
    width: '100vh',
    height: '100vh',
    zIndex: -10,
    position: 'fixed',
    left: 0,
    top: 0,
    [`@media (max-width: ${themes.breakpoints.xs}px)`]: {
      display: 'none',
    },
  },
  screen: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    width: '100vw',
    height: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#0C0C0C',
  },
  notificationsRoot: {
    height: '56px',
    background: 'red',
  },
}));

const App: React.FC = () => {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const accountState = useSelector((state:RootState) => state.account);
  const [loading, setLoading] = useState(true);
  const theme = useMantineTheme()
  const isMobileDevice = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`);
  const { initializeCleverTap } = useCleverTap();
  const navigate = useNavigate();
  // const selectedColorScheme = getCookie('tiqs-theme') as ColorScheme;
  // const [colorScheme, setColorScheme] = useState<ColorScheme>(selectedColorScheme);
  // useEffect(() => {
  //   setColorScheme(selectedColorScheme);
  // }, [selectedColorScheme]);
  // const toggleColorScheme = (value?: ColorScheme) => {
  //   const tempColorScheme = value || (colorScheme === 'dark' ? 'light' : 'dark');
  //   setColorScheme(tempColorScheme);
  //   setCookie('tiqs-theme', tempColorScheme);
  // };
  // useHotkeys([['mod+J', () => toggleColorScheme()]]);
  const location = useLocation();
  const mobileNumberFromUrl = window.location.search.split('mobile=')[1];

  useEffect(() => {
    if (mobileNumberFromUrl) {
      dispatch(setMobileNumber(mobileNumberFromUrl));
      navigate("/login", { replace: true });
      window.history.pushState({}, document.title, "/");
    }
  }, [location.search])

  useEffect(() => {
    initializeCleverTap()
  }, []);

  useEffect(() => {
    const authData = loadData('authorization');
    if (authData) {
      dispatch(verifyPrevTokenAndGetUserDetails(authData));
    } else {
      clearData();
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (accountState.authToken && !accountState.userDetails?.phone) {
      dispatch(getUserDetails());
    }
  }, [accountState.authToken]);

  useEffect(() => {
    if (accountState.status === 'success' && loading) {
      dispatch(setAccountStatus('idle'));
      setLoading(false);
    }
  }, [accountState.status]);

  return (
    // <ColorSchemeProvider
    // colorScheme={colorScheme}
    // toggleColorScheme={toggleColorScheme}
    // >
    <MantineProvider
      withGlobalStyles
      withNormalizeCSS
      theme={getCustomTheme("light")}
    >
      <Notifications autoClose={2000} limit={20} position={isMobileDevice ? "top-right" : "bottom-right"} />
      <GlobalStyles />
      <RouteManager />
      <Navbar />
      {loading ? (
        <div className={classes.screen}>
          <ScreenLoader />
        </div>
      ) : (
        <div className="pageWrapper">
          <Routes>
            <Route path="/login" element={<Signup />} />
            <Route path="/verify-otp" element={<SignupVerification />} />
            <Route element={<RequireAuth />}>
              <Route path="/" element={<Home />} />
              {/* Pan card has 2 screens/steps from dame slice reducer */}
              <Route path="/verify-pan" element={<PanCardVerify />} />
              <Route path="/pan-image-upload" element={<PanUpload />} />
              <Route path="/add-email" element={<AddEmail />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/link-bank-account" element={<LinkBankAccount />} />
              <Route path="/personal-details" element={<PersonalDetails />} />
              <Route
                path="/verify-image"
                element={<PersonalImageVerification />}
              />
              <Route
                path="/sign-onboarding-form"
                element={<SignOnboardingForm />}
              />
              <Route path="/aadhaar-esign" element={<AadhaarEsignForm />} />
              {/* this is not in use now */}
              <Route path="/change-dob" element={<ChangeDob />} />
              <Route path="/add-nominee" element={<NomineeDetails />} />
              <Route path="/add-income-details" element={<IncomeProofForm />} />
              <Route
                path="/income-proof/callback"
                element={<IncomeProofForm />}
              />
              <Route path="/kyc-verification" element={<KycVerification />} />
              <Route path="/completed" element={<Completed />} />
              <Route path="/modification" element={<Modification />} />
            </Route>
          </Routes>
        </div>
      )}
    </MantineProvider>
    // </ColorSchemeProvider>
  );
};

export default App;
